import React from 'react';
import { Panel, Group, Separator } from 'react-resizable-panels';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import TourRunner from '../Tour/TourRunner';

// Steps that introduce the three-panel playground UI. Mounted here in the
// shared layout so every DB playground (RecipeDb / FlavorDb / SpiceRx / etc.)
// gets the same walkthrough without each page wiring it independently.
//
// Desktop shows all three panels at once → three steps.
// Mobile shows the sidebar plus either the docs OR the code panel via a tab
// switcher — never both → two steps. Step 2 uses a combined selector so the
// spotlight lands on whichever of `main` / `code` is currently visible (the
// other one is `display:none` and gets skipped by findVisibleElement).
const PLAYGROUND_TOUR_STEPS = [
    {
        selector: '[data-tour="db-playground-sidebar"]',
        title: "Browse the available endpoints here",
        placement: "right",
    },
    {
        selector: '[data-tour="db-playground-main"]',
        title: "Configure parameters and run the request",
        placement: "right",
    },
    {
        selector: '[data-tour="db-playground-code"]',
        title: "See the generated request and live response here",
        placement: "left",
    },
];

// Mobile gets a single combined step. The reason: on mobile the sidebar
// expands to fill the viewport when no endpoint is selected, leaving the
// docs/code panels with zero visible height. A separate "docs/code" step
// would highlight an empty region and look broken. One step covering the
// sidebar with comprehensive text avoids that and still teaches the layout.
const PLAYGROUND_TOUR_STEPS_MOBILE = [
    {
        selector: '[data-tour="db-playground-sidebar"]',
        title:
            "Browse endpoints from this list. Tap any endpoint to configure parameters, then switch between Documentation and Code tabs to send the request and view the response.",
    },
];

const PlaygroundLayout = ({
    sidebar,
    mainContent,
    rightPanel,
    mobileActiveTab,
    sidebarOpen,
    className = ""
}) => {
    return (
        <>
            {/* Pill anchored top-right, below the navigation. */}
            <TourRunner
                steps={PLAYGROUND_TOUR_STEPS}
                mobileSteps={PLAYGROUND_TOUR_STEPS_MOBILE}
                pillTop={100}
                pillRight={24}
            />

            {/* Mobile View */}
            <div className="md:hidden h-full flex flex-col flex-1 overflow-hidden relative">
                <div data-tour="db-playground-sidebar">{sidebar}</div>
                <div className="flex flex-1 overflow-hidden">
                    <div data-tour="db-playground-main" className={`${mobileActiveTab === 'docs' ? 'flex' : 'hidden'} w-full overflow-y-auto px-4 md:px-6 py-4 pb-6 flex-col`}>
                        {mainContent}
                    </div>
                    <div data-tour="db-playground-code" className={`${mobileActiveTab === 'code' ? 'flex' : 'hidden'} w-full bg-gray-800 dark:bg-gray-900 h-full overflow-hidden flex-col border-l-0`}>
                        {rightPanel}
                    </div>
                </div>
            </div>

            {/* Desktop View with Resizable Panels */}
            <div className="hidden md:flex flex-1 overflow-hidden relative">
                <Group direction="horizontal" className="h-full">
                    {/* Sidebar Panel */}
                    <Panel
                        defaultSize="20"
                        minSize="10"
                        maxSize="30"
                        collapsible={true}
                        onCollapse={() => {
                            // Optional: notify parent if needed, or handle internal state
                        }}
                        className="border-r border-gray-200 dark:border-gray-700"
                    >
                        <div data-tour="db-playground-sidebar" className="h-full overflow-hidden flex flex-col">
                            {sidebar}
                        </div>
                    </Panel>

                    <Separator className="w-1 bg-gray-100 hover:bg-indigo-300 dark:bg-gray-800 dark:hover:bg-indigo-700 transition-colors cursor-col-resize flex items-center justify-center translate-x-0 z-10">
                        <div className="w-0.5 h-8 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                    </Separator>

                    {/* Main Content (Docs) Panel */}
                    <Panel defaultSize="45" minSize="30">
                        <div data-tour="db-playground-main" className="h-full overflow-y-auto px-4 md:px-6 py-4 pb-6 flex flex-col">
                            {mainContent}
                        </div>
                    </Panel>

                    <Separator className="w-1 bg-gray-100 hover:bg-indigo-300 dark:bg-gray-800 dark:hover:bg-indigo-700 transition-colors cursor-col-resize flex items-center justify-center z-10">
                        <div className="w-0.5 h-8 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                    </Separator>

                    {/* Right Panel (Code) */}
                    <Panel defaultSize="35" minSize="20" className="bg-gray-800 dark:bg-gray-900">
                        <div data-tour="db-playground-code" className="h-full overflow-hidden flex flex-col">
                            {rightPanel}
                        </div>
                    </Panel>
                </Group>
            </div>
        </>
    );
};

export default PlaygroundLayout;
